import { onBeforeUnmount, watch } from 'vue';
import { useNxCommands, type NxCommand } from '../CommandPalette/commands';
import type { NxWorkspaceHeaderMenuItem } from './NxWorkspaceHeader.vue';

/**
 * Puts the entries of a workspace header's ⋯ menu into the ⌘K palette
 * (I-2348), generated from the same item list the menu renders — picking one
 * calls the menu's own select handler. A disabled entry is hidden.
 */
export interface NxMenuCommandOptions {
  /** The app key; ids become `<app>.<prefix>.<item id>`. */
  app: string;
  /** Second id segment, e.g. `menu`. */
  prefix: string;
  /** Group heading in the palette (usually the app's name). */
  group: () => string;
  /** The menu's items, read on every change. */
  items: () => readonly NxWorkspaceHeaderMenuItem[];
  /** The menu's `menu-select` handler. */
  select: (id: string) => void;
  /** Extra condition for all entries (e.g. the view is ready). */
  when?: () => boolean;
  /** Search words added to every entry (the app's name, …). */
  keywords?: string[];
}

/** The palette entries for a menu's items (pure; see `useNxMenuCommands`). */
export function nxMenuCommands(options: NxMenuCommandOptions): NxCommand[] {
  const group = options.group();
  const enabled = (id: string) => {
    if (options.when && !options.when()) return false;
    const item = options.items().find((entry) => entry.id === id);
    return !!item && !item.disabled;
  };
  // A group heading sits on the group's first item; it applies to the rest.
  let heading: string | undefined;
  return options.items().map((item): NxCommand => {
    if (item.groupLabel !== undefined || item.separatorBefore) heading = item.groupLabel;
    return {
      id: `${options.app}.${options.prefix}.${item.id}`,
      label: item.label,
      hint: heading,
      group,
      app: options.app,
      icon: item.icon,
      category: 'action',
      keywords: options.keywords,
      when: () => enabled(item.id),
      run: () => options.select(item.id),
    };
  });
}

/** Registers `nxMenuCommands` while the calling component is mounted. */
export function useNxMenuCommands(options: NxMenuCommandOptions): void {
  const registry = useNxCommands();
  if (!registry) return;
  let unregister: () => void = () => {};
  watch(
    () =>
      [
        options.group(),
        ...options
          .items()
          .map((item) => `${item.id}\u0000${item.label}\u0000${item.groupLabel ?? ''}`),
      ].join('\n'),
    () => {
      unregister();
      unregister = registry.register(nxMenuCommands(options));
    },
    { immediate: true }
  );
  onBeforeUnmount(() => {
    unregister();
    unregister = () => {};
  });
}
